/**
 * Links that open a tutorial on the tablet: `https://tracker.bronze.vision/?tutorial=<name>`.
 *
 *  - `?tutorial=periscope` — the periscope install guide (PeriscopeTutorialScreen).
 *  - `?tutorial=good-photos` — the «Cómo hacer buenas fotos» video, in the tablet's language
 *    (PhotoVideoTutorialScreen, docs/tutorial-video-buenas-fotos.md).
 *
 * Why this exists: when a store writes to support because its photos come out wrong, the answer is
 * usually "place the periscope again" or "watch how to take a good photo", and both are buried in
 * Ajustes. Support pastes the link in the Intercom conversation and a tap on it opens it on that tablet.
 *
 * Two ways in:
 *  - The tap inside the messenger (the usual one). Intercom opens every link in a NEW tab, which on a
 *    tablet means a second copy of Tracker in Chrome. But the messenger's iframe is created without
 *    `src`, so it is same-origin and its document is ours (see src/intercomComposer.js): the click is
 *    caught there, the new tab never opens, and the tutorial opens in the app already running.
 *  - The app loaded with the link (opened from anywhere else, or typed): the query is read once at
 *    startup and removed from the address bar, so a reload does not open the tutorial again.
 *
 * Both Tracker production hosts are accepted, besides the page's own origin: support sends one link,
 * whatever host the tablet runs on. Nothing here throws.
 */

export const TUTORIAL_LINK_PARAM = 'tutorial';
export const TUTORIAL_LINKS = Object.freeze({ PERISCOPE: 'periscope', PHOTO_VIDEO: 'good-photos' });
export const TUTORIAL_LINK_HOSTS = Object.freeze(['tracker.bronze.vision', 'app.bronze.vision']);

const KNOWN = Object.values(TUTORIAL_LINKS);
const FRAME = 'iframe[name^="intercom"]';

/** The tutorial `href` opens in this app (`origin` is the page's own): one of TUTORIAL_LINKS, or null. */
export function tutorialLinkOf(href, origin) {
  try {
    const url = new URL(href, origin);
    const ours = url.origin === origin || (url.protocol === 'https:' && TUTORIAL_LINK_HOSTS.includes(url.hostname));
    const name = url.searchParams.get(TUTORIAL_LINK_PARAM);
    return ours && KNOWN.includes(name) ? name : null;
  } catch (_) {
    return null;
  }
}

/**
 * Was the app loaded with a tutorial link? If so, the query is removed from the address bar (the rest of
 * the URL and the history state are kept) and the tutorial is returned; null otherwise. Read it once, at
 * startup.
 */
export function takeTutorialLinkFromLocation(win = window) {
  try {
    const name = tutorialLinkOf(win.location.href, win.location.origin);
    if (!name) return null;
    const url = new URL(win.location.href);
    url.searchParams.delete(TUTORIAL_LINK_PARAM);
    win.history.replaceState(win.history.state, '', url.pathname + url.search + url.hash);
    return name;
  } catch (_) {
    return null;
  }
}

/**
 * Catch taps on a tutorial link inside the Intercom frames of `doc` and hand the tutorial to `onLink`
 * instead of letting the messenger open a new tab. Call it every time the messenger is shown: Intercom
 * writes its frames' documents itself and may rewrite them, which drops their listeners, so the listener
 * is put back (removed first, so it is never there twice). Returns how many frames it reached.
 */
export function catchTutorialLinks(doc, onLink) {
  let reached = 0;
  try {
    const origin = doc.defaultView.location.origin;
    const handler = (event) => {
      const target = event.target;
      const anchor = target && typeof target.closest === 'function' ? target.closest('a[href]') : null;
      const name = anchor ? tutorialLinkOf(anchor.getAttribute('href'), origin) : null;
      if (!name) return;
      event.preventDefault();
      event.stopPropagation();
      onLink(name);
    };
    for (const frame of doc.querySelectorAll(FRAME)) {
      let inner = null;
      try {
        inner = frame.contentDocument;
      } catch (_) {
        inner = null; // another origin: out of reach
      }
      if (!inner) continue;
      const previous = inner.__bzTutorialLinkHandler;
      if (previous) inner.removeEventListener('click', previous, true);
      inner.addEventListener('click', handler, true);
      inner.__bzTutorialLinkHandler = handler;
      reached += 1;
    }
  } catch (_) {
    /* best effort */
  }
  return reached;
}
